import { LocalStorageKeys } from '@/constants/localStorage';
import { setPhiBlurSource } from '@/utils/phiBlur';

/**
 * The "Hide PHI" preference, kept in localStorage so it lasts across reloads.
 *
 * It is per device on purpose, rather than a synced user setting: hiding PHI is about who can see
 * this screen right now, which says nothing about the staff member's other machines. A module
 * store lets the nav button and the keyboard shortcut share one value wherever they mount.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

// Answers reads for the rest of the session once localStorage has refused a write.
let unsavedHidePhi: boolean | null = null;

export const getHidePhi = (): boolean => {
  if (unsavedHidePhi !== null) return unsavedHidePhi;

  try {
    return localStorage.getItem(LocalStorageKeys.HIDE_PHI) === 'true';
  } catch {
    // Private browsing and embedded contexts can throw on access.
    return false;
  }
};

export const setHidePhi = (hidePhi: boolean) => {
  try {
    if (hidePhi) {
      localStorage.setItem(LocalStorageKeys.HIDE_PHI, 'true');
    } else {
      localStorage.removeItem(LocalStorageKeys.HIDE_PHI);
    }
    unsavedHidePhi = null;
  } catch {
    // Still hide for this session when the preference cannot be saved.
    unsavedHidePhi = hidePhi;
  }

  setPhiBlurSource('manual', hidePhi);
  listeners.forEach((listener) => listener());
};

export const subscribeHidePhi = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Puts the saved preference back on the page after a reload. */
export const applyStoredHidePhi = () => setPhiBlurSource('manual', getHidePhi());

/**
 * Follows the preference when another tab changes it. Without this, a second tab kept showing
 * PHI, and its button (still drawn as off) would read the shared `true` and turn hiding off.
 * The `storage` event only fires in the other tabs, never the one that wrote the value.
 */
export const syncHidePhiAcrossTabs = () => {
  const handleStorage = (event: { key: string | null }) => {
    // A null key means localStorage was cleared.
    if (event.key !== null && event.key !== LocalStorageKeys.HIDE_PHI) return;

    setPhiBlurSource('manual', getHidePhi());
    listeners.forEach((listener) => listener());
  };

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
};
